import { ScrollView, Text, View, Pressable } from 'react-native';
import { useCallback, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { LegendList } from "@legendapp/list";
import SearchBox from '@/components/General/inputs/SearchBox';
import Chip from '@/components/General/Chip';
import ContentLoader from '@/components/General/ContentLoader';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Avatar from '@/components/People/Avatar';
import { Query } from '@/utils/react-query/Queries';
import { recipientStatus } from './campaignHelpers';

/**
 * Recipients list shown inside the SMS Campaign detail sheet.
 * Fetches SMSCampaignRecipients for the campaign and renders a search box,
 * status filter chips (All / Sent / Failed / Skipped), and a scrollable list.
 */
const STATUS_FILTERS = ['all', 'sent', 'failed', 'skipped'];
const EMAIL_STATUS_FILTERS = ['all', 'pending', 'accepted', 'delivered', 'bounced', 'failed', 'skipped'];
const EMPTY_DELIVERY_STATUSES = {};
const statusColor = status => status === 'sent' || status === 'delivered' ? 'success' : status === 'failed' || status === 'bounced' || status === 'complained' || status === 'suppressed' ? 'error' : 'warning';

const initials = (name) => {
    if(!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if(parts.length === 0) return '?';
    if(parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const SMSCampaignRecipientsSection = ({ campaignID, channel = 'sms', hasHistory = true, deliveryEnabled = true }) => {
    const isEmail = channel === 'email';
    const smsQuery = Query.smsCampaigns.recipients({ campaignID: isEmail ? null : campaignID });
    const emailQuery = Query.emailCampaigns.recipients({ campaignID: isEmail && hasHistory ? campaignID : null });
    const deliveryQuery = Query.emailCampaigns.delivery({ campaignID: isEmail ? campaignID : null, enabled: hasHistory && deliveryEnabled });
    const { data: recipients, isLoading, isError, refetch } = isEmail ? emailQuery : smsQuery;
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const statuses = deliveryQuery.data?.statuses || EMPTY_DELIVERY_STATUSES;
    const reasons = deliveryQuery.data?.reasons || EMPTY_DELIVERY_STATUSES;
    const getStatus = useCallback(r => {
        if (!isEmail) return recipientStatus(r);
        const status = r.status === 'accepted' || r.status === 'pending' ? statuses[r.email?.trim().toLowerCase()] || r.status : r.status;
        return status === 'complained' || status === 'suppressed' ? 'failed' : status;
    }, [isEmail, statuses]);
    const filters = isEmail ? EMAIL_STATUS_FILTERS : STATUS_FILTERS;

    const filtered = useMemo(() => {
        const all = recipients || [];
        let result = statusFilter === 'all' ? all : all.filter(r => getStatus(r) === statusFilter);
        if(searchText) {
            const q = searchText.toLowerCase();
            result = result.filter(r =>
                r.name?.toLowerCase().includes(q) ||
                (isEmail ? r.email : r.phoneNumber)?.toLowerCase().includes(q) ||
                r.notes?.toLowerCase().includes(q)
            );
        }
        return result;
    }, [recipients, searchText, statusFilter, isEmail, getStatus]);

    const counts = useMemo(() => {
        const all = recipients || [];
        return Object.fromEntries(['all', ...filters.slice(1)].map(status => [status, status === 'all' ? all.length : all.filter(r => getStatus(r) === status).length]));
    }, [recipients, getStatus, filters]);

    const renderItem = ({ item }) => {
        const status = getStatus(item);
        const note = isEmail ? reasons[item.email?.trim().toLowerCase()] || item.notes : item.notes;
        const profile = item.Person || null;
        return (
            <View style={styles.row}>
                {profile ? (
                    <Avatar size={34} profile={profile} disableBorder />
                ) : (
                    <View style={[styles.avatar, styles.avatarColor(status)]}>
                        <Text style={styles.avatarText}>{initials(item.name)}</Text>
                    </View>
                )}
                <View style={styles.rowText}>
                    <View style={styles.rowTop}>
                        <Text style={styles.rowName} numberOfLines={1}>{item.name || 'Unknown'}</Text>
                        <Chip colorVariant={statusColor(status)} style={styles.statusChip}>
                            {status}
                        </Chip>
                    </View>
                    <Text style={styles.rowSub} numberOfLines={2}>
                        {(isEmail ? item.email || 'No email' : item.phoneNumber || 'No phone')}
                        {note ? ` · ${note}` : ''}
                    </Text>
                </View>
            </View>
        );
    };

    let contentLoaders = [];
    if(isLoading) {
        for(let i = 0; i < 6; i++) {
            contentLoaders.push(<View key={"rcpt-loader" + i} style={styles.loaderRow}><ContentLoader containerHeight={48} staggerPosition={i} /></View>);
        }
    }

    return (
        <View style={styles.container} testID={isEmail ? 'email-campaign-recipients-section' : 'sms-campaign-recipients-section'}>
            <View style={styles.header}>
                <Text style={styles.title}>Recipients</Text>
                {hasHistory && <Pressable testID="campaign-recipients-refresh" onPress={() => { refetch(); if (isEmail) deliveryQuery.refetch(); }}>
                    <Text style={styles.count}>Refresh · {counts.all} total</Text>
                </Pressable>}
            </View>
            {!hasHistory ? <Text style={styles.notice}>Recipient history is unavailable for campaigns sent before tracking was enabled.</Text> : <>
            {isEmail && <Text style={styles.notice}>Delivered means accepted by the receiving mail server, not necessarily opened. Accepted means delivery has not been confirmed yet.</Text>}
            {isEmail && deliveryQuery.isError && <Text style={styles.notice}>Live delivery status is temporarily unavailable. Showing send-attempt status.</Text>}
            {isError && <Text style={styles.notice}>Could not load recipients. Try refreshing.</Text>}
            <View style={styles.searchWrap}>
                <SearchBox
                    searchText={searchText}
                    onChangeText={setSearchText}
                    placeholder="Search recipients"
                    textChangeDelay={150}
                    testID="SMSCampaignRecipients"
                />
            </View>

            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterScroll} contentContainerStyle={styles.filterRow}>
                {filters.map(filter => (
                    <Pressable
                        key={filter}
                        testID={`recipient-filter-${filter}`}
                        onPress={() => setStatusFilter(filter)}
                        style={({ pressed }) => styles.filterChip({ isSelected: statusFilter === filter, pressed })}
                    >
                        <Text style={styles.filterChipText({ isSelected: statusFilter === filter })}>
                            {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                            {' '}
                            <Text style={styles.filterChipCount({ isSelected: statusFilter === filter })}>
                                {counts[filter]}
                            </Text>
                        </Text>
                    </Pressable>
                ))}
            </ScrollView>

            <View style={styles.listWrap}>
                {isLoading ? (
                    <View>{contentLoaders}</View>
                ) : !isError && (
                    <LegendList
                        style={styles.list}
                        recycleItems
                        estimatedItemSize={56}
                        keyExtractor={(item) => item.id}
                        keyboardShouldPersistTaps="handled"
                        showsVerticalScrollIndicator={false}
                        data={filtered}
                        extraData={deliveryQuery.data}
                        renderItem={renderItem}
                        ListFooterComponent={
                            filtered.length === 0
                                ? <EmptyListPlaceholder spacing={20} size={80} noLottie={false} message={searchText || statusFilter !== 'all' ? "No recipients match your filters" : "No recipients for this campaign"} />
                                : <View style={styles.footer} />
                        }
                    />
                )}
            </View>
            </>}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        flex: 1,
        minHeight: 0,
        gap: 8,
    },
    notice: {
        fontSize: 11,
        color: colors.textSecondary,
        fontFamily: fonts.body,
        lineHeight: 16,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'baseline',
        justifyContent: 'space-between',
    },
    title: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
    },
    count: {
        fontSize: 12,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
    },
    searchWrap: {
        marginHorizontal: -2,
    },
    filterScroll: {
        flexGrow: 0,
    },
    filterRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    filterChip: ({ isSelected, pressed }) => ({
        paddingVertical: 5,
        paddingHorizontal: 12,
        borderRadius: 16,
        backgroundColor: isSelected ? colors.primary : 'transparent',
        borderWidth: 1,
        borderColor: isSelected ? colors.primary : colors.border,
        opacity: pressed ? 0.7 : 1,
    }),
    filterChipText: ({ isSelected }) => ({
        fontFamily: fonts.bold,
        fontSize: 12,
        color: isSelected ? colors.onPrimary : colors.text,
    }),
    filterChipCount: ({ isSelected }) => ({
        fontFamily: fonts.heading,
        opacity: 0.8,
        color: isSelected ? colors.onPrimary : colors.textSecondary,
    }),
    listWrap: {
        // Use remaining modal height for recipients on web and native.
        // The detail sheet holds this list in its flex content area.
        flex: 1,
        minHeight: 0,
    },
    list: {
        flex: 1,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 8,
        paddingHorizontal: 4,
    },
    avatar: {
        width: 34,
        height: 34,
        borderRadius: 17,
        alignItems: 'center',
        justifyContent: 'center',
    },
    avatarColor: (status) => ({
        backgroundColor: statusColor(status) === 'success' ? colors.successContainer : statusColor(status) === 'error' ? colors.errorContainer : colors.warningContainer,
    }),
    avatarText: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    rowText: {
        flex: 1,
        gap: 2,
    },
    rowTop: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    rowName: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.text,
        flexShrink: 1,
    },
    rowSub: {
        fontSize: 11,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
    },
    statusChip: {
        textTransform: 'capitalize',
        flexShrink: 0,
    },
    loaderRow: {
        marginVertical: 4,
    },
    footer: {
        paddingBottom: 20,
    },
}));

export default SMSCampaignRecipientsSection;
